import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/sellerApi";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/stats"), api.get("/orders?period=week")])
      .then(([s, o]) => {
        setStats(s.data);
        setRecentOrders(o.data.slice(0, 10));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statusColors = {
    PENDING: "bg-gray-100 text-gray-600",
    ASSIGNED: "bg-blue-100 text-blue-700",
    PICKED_UP: "bg-yellow-100 text-yellow-700",
    IN_TRANSIT: "bg-purple-100 text-purple-700",
    DELIVERED: "bg-green-100 text-green-700",
    CANCELLED: "bg-red-100 text-red-600",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  const hasHistory = recentOrders.length > 0;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-green-600 to-green-700 text-white px-5 pt-6 pb-8 rounded-b-[32px] shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <span className="text-white text-lg font-bold tracking-wider">
            BYKA
          </span>
          <span className="text-green-200 text-[10px] font-semibold uppercase tracking-wider bg-green-800/40 px-3 py-1 rounded-full">
            User Mode
          </span>
        </div>
        <p className="text-green-200 text-sm">Hi {user?.name || "there"}</p>
        <h1 className="text-2xl font-bold mb-6">Need a package delivered?</h1>

        {/* Primary CTA Centerpiece */}
        <button
          onClick={() => navigate("/seller/new-order")}
          className="w-full bg-white text-green-700 hover:bg-gray-50 py-4.5 rounded-2xl font-extrabold text-lg shadow-xl shadow-green-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2.5 border border-white"
        >
          <svg
            className="w-6 h-6 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Send a delivery
        </button>
        <p className="text-center text-green-200 text-xs mt-3 font-medium">
          Usually delivered in under an hour
        </p>
      </div>

      {/* Conditionally render Stats or "How it works" */}
      {hasHistory ? (
        /* Quick Stats (Moved below the folds/CTA) */
        <div className="px-5 mt-6">
          <h2 className="font-semibold text-gray-400 mb-2.5 text-xs uppercase tracking-wider">
            Your Dashboard
          </h2>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {stats?.deliveries_today || 0}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Today</p>
              </div>
              <div className="border-l border-r border-gray-100">
                <p className="text-2xl font-bold text-green-600">
                  {stats?.deliveries_this_month || 0}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">This Month</p>
              </div>
              <div>
                <p className="text-sm font-bold text-green-600">
                  {stats?.discount_tier}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">Discount Tier</p>
                {stats?.deliveries_until_next_tier > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">
                    {stats.deliveries_until_next_tier} more to next tier
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* How It Works Explainer for Zero-Order Users */
        <div className="px-5 mt-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 text-base mb-4">
              How it works
            </h3>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm shrink-0 border border-emerald-100">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    Tell us pickup & drop-off
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Specify where to collect and deliver your package.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm shrink-0 border border-emerald-100">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    Match with a rider
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    We automatically assign the nearest available motorcycle
                    rider.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center font-bold text-sm shrink-0 border border-emerald-100">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-sm">
                    Track & complete
                  </h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Follow the delivery in real-time and pay securely upon
                    completion.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Orders */}
      <div className="px-5 mt-6 pb-20">
        <div className="flex justify-between items-center mb-3">
          <h2 className="font-semibold text-gray-400 text-xs uppercase tracking-wider">
            Recent Deliveries
          </h2>
          {hasHistory && (
            <Link
              to="/seller/deliveries"
              className="text-xs text-green-600 font-bold hover:text-green-700 uppercase tracking-wider"
            >
              View all
            </Link>
          )}
        </div>
        <div className="space-y-2">
          {recentOrders.map((o) => (
            <Link
              key={o.id}
              to={`/seller/order/${o.id}`}
              className="block bg-white rounded-xl border border-gray-100 p-4 shadow-sm hover:border-gray-200 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">
                    {o.buyer_name}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {o.dropoff_address}
                  </p>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase ${statusColors[o.status]}`}
                  >
                    {o.status}
                  </span>
                  <p className="text-sm font-extrabold text-gray-900 mt-1.5">
                    ${parseFloat(o.final_fee).toFixed(2)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
          {recentOrders.length === 0 && (
            <div className="text-center bg-white rounded-2xl border border-gray-100 py-10 px-4">
              <div className="w-12 h-12 bg-gray-50 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-gray-400 dark:text-gray-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                  />
                </svg>
              </div>
              <p className="text-gray-400 text-sm">
                No deliveries yet. Ready to send your first package?
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
